import { useEffect, useRef, useState } from "react";
import { Minus, Sparkle, X } from "@phosphor-icons/react";
import AskEdujac from "@/components/AskEdujac";

const CONFIGS = {
  school_admin: {
    apiPath: "/school/assistant/ask",
    subtitle: "Ask questions about your school data, compare performance and discover what needs attention.",
    placeholder: "Ask EDUJAC about attendance, students, fees, exams…",
    examples: [
      "What needs my attention today?",
      "How is attendance today?",
      "Compare attendance with last month",
      "Which students have attendance below 75%?",
      "How much fee is outstanding?",
      "Are any exam marks incomplete?",
    ],
  },
  teacher: {
    apiPath: "/teacher/assistant/ask",
    subtitle: "Ask questions about your assigned classes, subjects and students.",
    placeholder: "Ask EDUJAC about your classes, students, attendance…",
    examples: [
      "What needs my attention today?",
      "Prepare my class for PTM",
      "Who needs attention before PTM?",
      "Which classes do I handle?",
      "Show attendance summary for my classes",
      "Which of my students have attendance below 75%",
      "Which of my classes have attendance not marked?",
      "Which marks are incomplete for my classes?",
    ],
  },
};

export default function FloatingAskEdujac({ role, onNavigate }) {
  const cfg = CONFIGS[role];
  const [open, setOpen] = useState(false);
  const fabRef = useRef(null);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const panelDragRef = useRef(null);
  const wasDraggedRef = useRef(false);
  const [position, setPosition] = useState(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const [hiddenForDocumentAction, setHiddenForDocumentAction] = useState(false);

  useEffect(() => {
    if (!cfg) return undefined;

    const isPrintRoute = () =>
      window.location.pathname.startsWith("/print/");

    const hasVisibleHideMarker = () =>
      Array.from(
        document.querySelectorAll('[data-hide-ask-edujac="true"]')
      ).some((element) => element.getClientRects().length > 0);

    const syncVisibility = () => {
      const shouldHide = isPrintRoute() || hasVisibleHideMarker();
      setHiddenForDocumentAction(shouldHide);

      if (shouldHide) {
        setOpen(false);
      }
    };

    const handleBeforePrint = () => {
      setOpen(false);
      setHiddenForDocumentAction(true);
    };

    const handleAfterPrint = () => {
      syncVisibility();
    };

    syncVisibility();

    const observer = new MutationObserver(syncVisibility);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "data-hide-ask-edujac"],
    });

    window.addEventListener("resize", syncVisibility);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncVisibility);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [cfg]);

  if (!cfg || hiddenForDocumentAction) return null;

  const handleNavigate = (tab) => {
    setOpen(false);
    onNavigate && onNavigate(tab);
  };
  const startDrag = (e) => {
  if (window.innerWidth < 768) return;

  const rect = fabRef.current?.getBoundingClientRect();
  if (!rect) return;

  dragRef.current = {
    offsetX: e.clientX - rect.left,
    offsetY: e.clientY - rect.top,
  };
  wasDraggedRef.current = false;

  const move = (ev) => {
    if (!dragRef.current) return;
    wasDraggedRef.current = true;
    const size = 56;
    const left = Math.min(
      Math.max(8, ev.clientX - dragRef.current.offsetX),
      window.innerWidth - size - 8
    );
    const top = Math.min(
      Math.max(8, ev.clientY - dragRef.current.offsetY),
      window.innerHeight - size - 8
    );

    setPosition({ left, top });
  };

  const stop = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop);
};

const startPanelDrag = (e) => {
  if (e.target.closest("button, input, textarea, select, a")) return;
  if (window.innerWidth < 768) return;

  const rect = panelRef.current?.getBoundingClientRect();
  if (!rect) return;

  panelDragRef.current = {
    offsetX: e.clientX - rect.left,
    offsetY: e.clientY - rect.top,
  };

  const move = (ev) => {
    if (!panelDragRef.current) return;

    const panelWidth = rect.width;
    const panelHeight = rect.height;

    const left = Math.min(
      Math.max(8, ev.clientX - panelDragRef.current.offsetX),
      window.innerWidth - panelWidth - 8
    );

    const top = Math.min(
      Math.max(8, ev.clientY - panelDragRef.current.offsetY),
      window.innerHeight - panelHeight - 8
    );

    setPanelPosition({ left, top });
  };

  const stop = () => {
    panelDragRef.current = null;
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
  };

  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop);
};

  return (
    <>
      <button
        data-testid="ask-fab"
        ref={fabRef}
        onPointerDown={startDrag}
        style={
          position
            ? {
                left: `${position.left}px`,
                top: `${position.top}px`,
                right: "auto",
                bottom: "auto",
              }
            : undefined
}
        onClick={() => {
         if (wasDraggedRef.current) {
          wasDraggedRef.current = false;
          return;
        }
        setOpen((o) => !o);
      }}
        aria-label="Ask EDUJAC"
        className="fixed z-50 bottom-5 right-5 md:bottom-6 md:right-6 w-14 h-14 rounded-full bg-brand-coral text-white shadow-xl shadow-brand-coral/30 flex items-center justify-center hover:bg-brand-coralDark transition-all duration-300 hover:scale-105 active:scale-95"
      >
        {open ? <X size={22} weight="bold" /> : <Sparkle size={24} weight="fill" />}
      </button>

      <div
        ref={panelRef}
        data-testid="ask-panel"
        style={
          panelPosition && window.innerWidth >= 768
            ? {
                left: `${panelPosition.left}px`,
                top: `${panelPosition.top}px`,
                right: "auto",
                bottom: "auto",
              }
            : undefined
  }
        className={`fixed z-50 flex-col bg-white overflow-hidden shadow-2xl transition-all duration-300 origin-bottom-right
          inset-0 h-[100dvh] md:inset-auto md:bottom-24 md:right-6 md:w-[400px] md:h-[min(640px,calc(100dvh-140px))] md:rounded-2xl md:border md:border-slate-200
          ${open ? "flex opacity-100 scale-100" : "hidden opacity-0 scale-95"}`}
      >
        <div
           onPointerDown={startPanelDrag}
           className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100 bg-white shrink-0 md:cursor-move"
        >
          <span className="w-8 h-8 rounded-full bg-brand-coral/10 flex items-center justify-center shrink-0">
            <Sparkle size={16} weight="fill" className="text-brand-coral" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-extrabold text-[#00194E] leading-tight">Ask EDUJAC</p>
            <p className="text-[11px] text-slate-500 truncate">{cfg.subtitle}</p>
          </div>
          <button
            data-testid="ask-minimize-btn"
            onClick={() => setOpen(false)}
            aria-label="Minimize Ask EDUJAC"
            title="Minimize Ask EDUJAC"
            className="p-2.5 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 focus-visible:outline-2 focus-visible:outline-brand-coral transition-colors duration-200"
          >
            <Minus size={18} weight="bold" />
          </button>
          <button
            data-testid="ask-close-btn"
            onClick={() => setOpen(false)}
            aria-label="Close Ask EDUJAC"
            title="Close Ask EDUJAC"
            className="p-2.5 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 focus-visible:outline-2 focus-visible:outline-brand-coral transition-colors duration-200"
          >
            <X size={18} />
          </button>
        </div>
        <AskEdujac
          onNavigate={handleNavigate}
          apiPath={cfg.apiPath}
          examples={cfg.examples}
          placeholder={cfg.placeholder}
        />
      </div>
    </>
  );
}
